<template>
    <div>
        <div v-if="grids.length">
            <BTabs card pills @activate-tab="onTabShown">
                <BTab
                    v-for="g in grids"
                    :key="g.id"
                    :title="renderTabTitle(g)"
                >
                    <div class="mb-2 d-flex justify-content-between align-items-center">
                        <div class="text-muted small">
                            {{ (g.records_push || []).length }} запис(ів)
                        </div>
                        
                        <BButton
                            variant="outline-danger"
                            size="sm"
                            @click="destroyGrid(g.id)"
                            title="Прибрати весь таб"
                        >
                            Прибрати розподілення {{ renderTabTitle(g) }}
                        </BButton>
                    </div>
                    
                    <div class="d-block">
                        <div class="ddd">
                            <div class="border rounded p-3 bg-light">
                                <div class="fw-semibold mb-2">
                                    {{ renderTabTitle(g) }}
                                </div>
                                
                                <div class="d-flex align-items-baseline gap-2 mb-2">
                                    <span class="text-muted">Всього (грн):</span>
                                    <span class="fs-5 fw-bold">{{ summaryFor(g).totalUAH }}</span>
                                </div>
                                
                                <div class="small mb-1">
                                    <div>
                                        <template v-if="summaryFor(g).usdRate">
                                            {{ summaryFor(g).usdRate }}
                                        </template>
                                    </div>
                                    <div>
                                        <template v-if="summaryFor(g).eurRate">
                                            {{ summaryFor(g).eurRate }}
                                        </template>
                                    </div>
                                </div>
                                
                                <div v-if="summaryFor(g).payment" class="small mb-1">
                                    <span class="text-muted">Умови оплати: </span>
                                    <span class="ms-1">{{ summaryFor(g).payment }}</span>
                                </div>
                                
                                <div v-if="summaryFor(g).delivery" class="small mb-1">
                                    <span class="text-muted">Умови поставки: </span>
                                    <span class="ms-1">{{ summaryFor(g).delivery }}</span>
                                </div>
                                
                                <div class="small" v-if="summaryFor(g).positioninfo">
                                    <span class="text-muted">Примітка: </span>
                                    <span
                                        class="ms-1"
                                        v-if="isHtml(summaryFor(g).positioninfo)"
                                        v-html="summaryFor(g).positioninfo"
                                    ></span>
                                    <span class="ms-1" v-else>
                                        {{ summaryFor(g).positioninfo }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <BTable
                        :items="itemsFor(g)"
                        :fields="toFields(g.columns)"
                        small
                        striped
                        responsive
                        primary-key="_pk"
                        head-variant="light"
                    >
                        <template
                            v-for="f in toFields(g.columns)"
                            :key="`slot_${f.key}`"
                            v-slot:[`cell(${f.key})`]="ctx"
                        >
                            <template v-if="f.key !== '_actions'">
                                <template v-if="isQtyProtocolKey(f.key)">
                                    <div class="w-100" @dblclick.stop="startCellEdit(ctx.item, f.key)">
                                        <template v-if="isCellEditing(ctx.item, f.key)">
                                            <input
                                                type="number"
                                                class="form-control form-control-sm"
                                                v-model.number="ctx.item[f.key]"
                                                @click.stop
                                                @dblclick.stop
                                                @blur="finishCellEdit(ctx.item, f.key)"
                                                @keyup.enter="finishCellEdit(ctx.item, f.key)"
                                                @keyup.esc="cancelCellEdit(ctx.item, f.key)"
                                            />
                                        </template>
                                        
                                        <template v-else>
                                            <span
                                                v-if="isHtml(ctx.item?.[f.key])"
                                                v-html="ctx.item?.[f.key]"
                                            ></span>
                                            <span v-else>{{ ctx.item?.[f.key] }}</span>
                                        </template>
                                    </div>
                                </template>
                                
                                <template v-else>
                                    <span
                                        v-if="isHtml(ctx.item?.[f.key])"
                                        v-html="ctx.item?.[f.key]"
                                    ></span>
                                    <span v-else>{{ ctx.item?.[f.key] }}</span>
                                </template>
                            </template>
                        </template>
                        
                        <template #cell(_actions)="ctx">
                            <BButton
                                size="sm"
                                variant="outline-danger"
                                @click="removeRow(ctx.item.__gridId, ctx.item)"
                                title="Прибрати позицію"
                            >
                                ×
                            </BButton>
                        </template>
                    </BTable>
                    
                    <div v-if="hasSumCols(g.columns)" class="text-end mt-2">
                        <div
                            v-for="col in sumCols(g.columns)"
                            :key="col.key"
                            class="small"
                        >
                            <strong>{{ col.label }}:</strong>
                            {{ sumColumn(itemsFor(g), col.key) }}
                        </div>
                    </div>
                </BTab>
            </BTabs>
        </div>
        
        <div v-else class="text-center text-muted py-5 border rounded">
            <i class="far fa-folder-open fa-2x d-block mb-2"></i>
            Позиції не розподілені
        </div>
        
        <div id="positions_control" class="mt-3">
            <BButton
                variant="outline-primary"
                class="btn_auto_render"
                @click="auto_distribute"
            >
                Розподілити
            </BButton>
        </div>
    </div>
</template>

<script>
import {
    BTabs,
    BTab,
    BTable,
    BOverlay,
    BSpinner,
    BButton,
    BButtonGroup
} from 'bootstrap-vue-next';

export default {
    name: 'protocol-winners2',
    
    components: {
        BTabs,
        BTab,
        BTable,
        BOverlay,
        BSpinner,
        BButton,
        BButtonGroup
    },
    
    props: {
        protocol: { type: Object, required: true },
    },
    
    watch: {
        protocol: {
            immediate: true,
            deep: true,
            handler() {
                this.initWinnerGrids();
            }
        }
    },
    
    computed: {
        grids() {
            return this.protocol?.position_grid_add || [];
        },
        
        appsInfo() {
            return this.protocol?.applications_info || [];
        },
        
        tenderPositions() {
            return this.protocol?.tender?.positions || [];
        },
        
        savedWinners() {
            return this.protocol?.memorandum?.winners || [];
        },
    },
    
    mounted() {
        if (!Array.isArray(this.protocol.position_grid_add)) {
            this.protocol.position_grid_add = [];
        }
        
        if (!this.protocol.position_grid_add.length) {
         //   this.protocol.position_grid_add = this.buildGridsFromWinners();
        }
    },
    
    methods: {
        initWinnerGrids() {
            
            if (!this.protocol) {
                return;
            }
            
            if (!Array.isArray(this.protocol.position_grid_add)) {
                this.protocol.position_grid_add = [];
            }
            
            if (this.protocol.position_grid_add.length) {
                return;
            }
            
            const winners = this.protocol?.memorandum?.winners || [];
            const positions = this.protocol?.tender?.positions || [];
            
            if (!winners.length || !positions.length) {
                return;
            }
            
            this.auto_distribute();
        },
        
        
        buildGridsFromWinners() {
            const winners = Array.isArray(this.savedWinners) ? this.savedWinners : [];
            
            if (!winners.length) {
                return [];
            }
            
            return winners.map((winner, index) => {
                return {
                    id: 'winner_' + index,
                    title: winner.counterparty || ('Winner ' + index),
                    columns: [
                        { field: 'counterparty', caption: 'Учасник' },
                        { field: 'edrpou', caption: 'ЄДРПОУ' },
                        { field: 'reason', caption: 'Обґрунтування' },
                    ],
                    summary: [],
                    records_push: [
                        {
                            _pk: index,
                            counterparty: winner.counterparty,
                            edrpou: winner.edrpou,
                            reason: winner.reason,
                            __gridId: 'winner_' + index
                        }
                    ]
                };
            });
        },
        
        buildSingleGridFromWinner(winner, positions) {
            const gridId = String(winner?.app || '');
            const appId = gridId.replace(/^app/, '');
            
            console.log('winner raw', winner);
            console.log('gridId', gridId, 'appId', appId);
            
            if (!gridId || !appId) {
                console.log('skip: empty gridId/appId');
                return null;
            }
            
            const appMeta = this.findAppMeta(appId);
            const winnerRows = Array.isArray(winner?.win) ? winner.win : [];
            
            console.log('winnerRows', winnerRows);
            console.log('appMeta', appMeta);
            
            if (!winnerRows.length) {
                console.log('skip: winnerRows empty');
                return null;
            }
            
            const records_push = winnerRows
                .map((winRow) => {
                    const tenderPositionId = String(winRow?.tender_position_id || '');
                    
                    console.log('winRow', winRow);
                    console.log('tenderPositionId', tenderPositionId);
                    console.log('all position ids', positions.map(p => String(p?.id)));
                    
                    const src = positions.find(p => String(p?.id) === tenderPositionId);
                    
                    console.log('matched src', src);
                    
                    if (!src) {
                        return null;
                    }
                    
                    const row = JSON.parse(JSON.stringify(src));
                    
                    const qtyKey = 'quantity_protocol' + appId;
                    const sumKey = 'Sum_' + appId;
                    const priceKey = 'Price_' + appId;
                    const valPriceKey = 'ValPrice_' + appId;
                    const appKey = 'app' + appId;
                    
                    const quantity = parseFloat(winRow?.quantity || 0);
                    const price = parseFloat(src?.[priceKey] || 0);
                    const sum = parseFloat(winRow?.summ || (price * quantity) || 0);
                    
                    row[qtyKey] = quantity;
                    row[sumKey] = +sum.toFixed(2);
                    
                    if (!row[priceKey] && price) {
                        row[priceKey] = price;
                    }
                    
                    if (!row[valPriceKey] && price) {
                        row[valPriceKey] = price;
                    }
                    
                    if (!row[appKey]) {
                        row[appKey] = sum;
                    }
                    
                    row._pk = String(row?.recid ?? row?.id);
                    row.__gridId = gridId;
                    
                    return row;
                })
                .filter(Boolean);
            
            console.log('records_push', records_push);
            
            if (!records_push.length) {
                console.log('skip: no matched records_push');
                return null;
            }
            
            return {
                id: gridId,
                title: appMeta?.company || appMeta?.title || gridId,
                columns: this.buildGridColumns(appId),
                summary: this.buildGridSummary(appId, appMeta, records_push),
                records_push,
            };
        },
        
        buildGridColumns(appId) {
            return [
                { field: 'title', caption: 'Позиція' },
                { field: 'quantity_protocol' + appId, caption: 'Кількість' },
                { field: 'price_val_' + appId, caption: 'Ціна' },
                { field: 'Sum_' + appId, caption: 'Сума(грн)' },
            ];
        },
        
        buildGridSummary(appId, appMeta = null, rows = []) {
            const total = rows.reduce((acc, row) => {
                return acc + (parseFloat(row?.['Sum_' + appId]) || 0);
            }, 0);
            
            const usdRate = appMeta?.usd_exchange_rate && parseFloat(appMeta.usd_exchange_rate) > 0
                ? `Курс USD: ${appMeta.usd_exchange_rate}`
                : '';
            
            const eurRate = appMeta?.eur_exchange_rate && parseFloat(appMeta.eur_exchange_rate) > 0
                ? `Курс EUR: ${appMeta.eur_exchange_rate}`
                : '';
            
            return [
                {
                    recid: 'S-1',
                    ['app' + appId]: total,
                    ['Sum_' + appId]: total,
                },
                {
                    recid: 'S-2',
                    ['price_val_' + appId]: usdRate,
                    ['quantity_' + appId]: appMeta?.paymentinfo || '',
                },
                {
                    recid: 'S-3',
                    ['price_val_' + appId]: eurRate,
                    ['quantity_' + appId]: appMeta?.deliveryinfo || '',
                },
            ];
        },
        
        findAppMeta(appId) {
            return (this.appsInfo || []).find(x => String(x?.id) === String(appId)) || null;
        },
        
        isQtyProtocolKey(key) {
            return typeof key === 'string' && key.startsWith('quantity_protocol');
        },
        
        isCellEditing(item, key) {
            return item?.__editingCell === key;
        },
        
        startCellEdit(item, key) {
            if (!item) return;
            item.__editingCell = key;
            item.__oldCellValue = item[key];
        },
        
        finishCellEdit(item, key) {
            if (!item) return;
            
            item.__editingCell = null;
            
            const appId = String(item?.__gridId || '').replace(/^app/, '');
            if (!appId) return;
            
            const qtyKey = key;
            const priceKey = 'Price_' + appId;
            const sumKey = 'Sum_' + appId;
            const appKey = 'app' + appId;
            
            const qty = parseFloat(item?.[qtyKey] || 0);
            const price = parseFloat(item?.[priceKey] || 0);
            const sum = +(qty * price).toFixed(2);
            
            item[sumKey] = sum;
            item[appKey] = sum;
        },
        
        cancelCellEdit(item, key) {
            if (!item) return;
            item[key] = item.__oldCellValue;
            item.__editingCell = null;
        },
        
        stripHtml(s) {
            return typeof s === 'string'
                ? s.replace(/<[^>]*>/g, '').trim()
                : s;
        },
        
        pickCell(row, key) {
            if (!row) return null;
            return row?.[key] ?? null;
        },
        
        asText(v) {
            if (v == null) return null;
            const s = typeof v === 'string' ? v : String(v);
            return this.stripHtml(s).trim() || null;
        },
        
        renderTabTitle(g) {
            return g?.title?.replace?.(/^<i.*?<\/i>\s*/i, '') || g?.title || g?.id || '';
        },
        
        itemsFor(g) {
            const rows = Array.isArray(g?.records_push) ? g.records_push : [];
            
            for (const r of rows) {
                r._pk = String(r?.recid ?? r?.id);
                r.__gridId = g?.id;
            }
            
            return rows;
        },
        
        isHtml(val) {
            if (typeof val !== 'string') return false;
            return /<\/?[a-z][\s\S]*>/i.test(val.trim());
        },
        
        toFields(cols = []) {
            const base = cols.map((c, i) => {
                const key = String(c?.field ?? c?.name ?? `col_${i}`);
                const label = c?.text || c?.caption || c?.app_title || key;
                
                return {
                    key,
                    label,
                    class: 'align-middle',
                    thClass: 'text-nowrap',
                    sortable: false,
                };
            });
            
            base.push({
                key: '_actions',
                label: '',
                tdClass: 'text-end',
                thStyle: { width: '1%' }
            });
            
            return base;
        },
        
        formatMoney(n) {
            const x = Number(n || 0);
            return x.toLocaleString('uk-UA', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
        },
        
        summaryFor(g) {
            const appId = String(g?.id || '').replace(/^app/, '');
            const items = this.itemsFor(g);
            
            const total = items.reduce((acc, r) => {
                return acc + (parseFloat(r?.['Sum_' + appId]) || 0);
            }, 0);
            
            const s3 = (g?.summary || []).find(r => String(r?.recid) === 'S-3');
            const s2 = (g?.summary || []).find(r => String(r?.recid) === 'S-2');
            
            const usdRate = this.pickCell(s2, 'price_val_' + appId);
            const eurRate = this.pickCell(s3, 'price_val_' + appId);
            
            const payment = this.pickCell(s2, 'quantity_' + appId);
            const delivery = this.pickCell(s3, 'quantity_' + appId);
            
            const meta = this.findAppMeta(appId);
            
            return {
                totalUAH: this.formatMoney(total),
                usdRate: this.asText(usdRate),
                eurRate: this.asText(eurRate),
                delivery: this.asText(delivery),
                payment: this.asText(payment),
                positioninfo: this.asText(meta?.positioninfo) || null
            };
        },
        
        onTabShown() {
            this.$nextTick(() => {
                EventBus?.$emit?.('tab-shown', 'protocol_winners');
            });
        },
        
        auto_distribute() {
            EventBus?.$emit?.('winners:auto_distribute');
        },
        
        destroyGrid(gridId) {
            const list = Array.isArray(this.protocol.position_grid_add)
                ? this.protocol.position_grid_add
                : [];
            
            const idx = list.findIndex(x => x.id === gridId);
            
            if (idx > -1) {
                list.splice(idx, 1);
            }
            
            EventBus?.$emit?.('winner_item_removed', { gridId });
        },
        
        removeRow(gridId, row) {
            const list = Array.isArray(this.protocol.position_grid_add)
                ? this.protocol.position_grid_add
                : [];
            
            const g = list.find(x => x.id === gridId);
            if (!g) return;
            
            const key = String(row?.recid ?? row?.id);
            
            g.records_push = (g.records_push || []).filter(
                r => String(r?.recid ?? r?.id) !== key
            );
            
            EventBus?.$emit?.('winner_item_removed', { gridId, record: key });
            
            if ((g.records_push || []).length === 0) {
                const idx = list.findIndex(x => x.id === gridId);
                if (idx > -1) {
                    list.splice(idx, 1);
                }
            }
        },
        
        hasSumCols(cols = []) {
            return cols.some(c => /^Sum_\d+$/i.test(c?.field || ''));
        },
        
        sumCols(cols = []) {
            return cols
                .filter(c => /^Sum_\d+$/i.test(c?.field || ''))
                .map(c => {
                    const key = String(c?.field || '');
                    return {
                        key,
                        label: c?.text || c?.caption || key
                    };
                });
        },
        
        
        sumColumn(items = [], key) {
            const s = items.reduce((acc, r) => acc + (parseFloat(r?.[key]) || 0), 0);
            return s.toFixed(2);
        },
    },
};
</script>

<style scoped>
</style>